import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";

import { useApolloClient } from "@apollo/client";

import { Select, SelectProps } from "@cofano/cck";

import FavoriteSelectOption from "components/selects/FavoriteSelectOption";
import { getLocationFavorites } from "components/selects/utils";

import {
	LocationsBySearchForSelectDocument,
	LocationsBySearchForSelectQuery,
	LocationsBySearchForSelectQueryVariables,
} from "__generated__/view/locations/graphql/location.queries.generated";
import { FavoriteType, LocationType, SortDirection } from "__generated__/enums";
import { ChildPropsFromPagination, Favorite } from "types/commons";

import {
	getLocationTypeByNumber,
	getLocationTypeNumber,
	locationDescriptionWithGroup,
} from "view/locations/utils/LocationUtils";

import { hasPluginPermission } from "imports/Utils";

type BaseLocationOption = ChildPropsFromPagination<LocationsBySearchForSelectQuery, "locationsBySearch">;

export type LocationSelectOption = Omit<BaseLocationOption, "type" | "locationGroups"> & {
	type?: LocationType | null;
    locationGroups?: NonNullable<BaseLocationOption["locationGroups"]> | null;
};

export type LocationSelectProps<Multiple extends boolean,> = {
	types?: Array<LocationType>;
	includeQuays?: boolean;
	locationGroup?: {
		id: number;
	}
	additionalSearchOptions?: NonNullable<LocationsBySearchForSelectQueryVariables["locationSearch"]>;
} & Omit<SelectProps<LocationSelectOption, Multiple>, "fetchOptions" | "paginated" | "getOptionLabel" | "renderOption">;

const LocationSelect = <Multiple extends boolean = false,>({
	types,
	includeQuays,
	locationGroup,
	additionalSearchOptions,
	...rest
}: LocationSelectProps<Multiple>) => {

	const session = useStoreState(state => state.session);
	const showGroup = hasPluginPermission(session, "showLocationGroup");
	const showType = hasPluginPermission(session, "showLocationType");

	const client = useApolloClient();
	const sessionFavorites = useStoreState((store) => store.session.user?.properties.favorites);
	const favorites = getLocationFavorites(sessionFavorites ?? [], types);

	const onFetchOptions = async(params: string, page: number) => {
		try  {

			const currentPage = page - 1;

			const result = await client.query({
				query: LocationsBySearchForSelectDocument,
				variables: {
					locationSearch: {
						...additionalSearchOptions,
						description: params,
						types: types,
						locationGroupId: locationGroup ? locationGroup.id : undefined,
					},
					pagination: {
						page: currentPage,
						size: 8,
						sortList: [ {
							field: "description",
							direction: SortDirection.Asc,
						} ],
					},
					includeQuays: includeQuays || false,
				},
			});

			const options: LocationSelectOption[] = [];

			if (currentPage === 0 && !params) {
				favorites.forEach((favorite: Favorite) => {

					const type = typeof favorite.location?.type === "number" ? favorite.location.type : getLocationTypeNumber(favorite.location?.type as LocationType);

					options.push({
						id: favorite.entityId,
						description: favorite.label || "unknown",
						type: getLocationTypeByNumber(type),
						locationGroups: [],
					});
				});
			}

			result.data.locationsBySearch.content.forEach(item => options.push(item as LocationSelectOption));

			return {
				options: options,
				totalResults: result.data.locationsBySearch.totalElements,
			};
		} catch (response) {
			return {
				options: [],
				totalResults: 0,
			};
		}
	};

	return (
		<Select
			fetchOptions={onFetchOptions}
			paginated
			getOptionLabel={option => showGroup ? locationDescriptionWithGroup(option) : option.description}
			typeToSearchLabel={intl.formatMessage({ id: "TYPE_TO_SEARCH" })}
			loadingLabel={intl.formatMessage({ id: "LOADING_DATA" })}
			renderOption={(props, option) => {
				return (
					<li {...props}>
						<FavoriteSelectOption
							entityId={option.id}
							entityType={FavoriteType.Location}
							subtitle={showType ? intl.formatMessage({ id: option.type?.toString() }) : undefined}>
							{option.description}

							{showGroup && option.locationGroups && option.locationGroups.length > 0 && (
								<small>
									<br />
									{option.locationGroups.find(lg => lg.type == LocationType.Port)?.description}
								</small>
							)}
						</FavoriteSelectOption>
					</li>
				);
			}}
			popoverHeight={300}
			{...rest} />
	);
};

export default LocationSelect;
